// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
// Batch: workAtHeightAccess (working-at-height, mobile-tower-scaffold,
// mewp-cherry-picker, ladders-stepladders).

import type { TriggerQuestion } from "./types";
import {
  OVERHEAD,
  FALLING_OBJECTS,
  LADDER_INSTABILITY,
  PLANT_PEDESTRIAN,
  ACCESS_OPTIONS,
  RESCUE_Q,
} from "./shared";

// Task-specific hazard ids minted for this batch (not in the shared canon).
const ACCESS_UNSUITED = "haz-working-at-height-access-method-unsuited-to-task-or-surface";
const ADVERSE_WEATHER = "haz-working-at-height-work-at-height-in-adverse-weather-or-high-wind";
const TOWER_UNGUARDED = "haz-working-at-height-tower-on-unguarded-platform-during-assembly";
const TOWER_OVERTURN = "haz-working-at-height-tower-overturn-from-overloading-or-height-to-base-ratio";
const TOWER_MOVED_OCCUPIED = "haz-working-at-height-tower-moved-while-occupied-or-castor-brake-off";
const DAMAGED_COMPONENT = "haz-working-at-height-mismatched-or-damaged-access-equipment-component";
const MEWP_ENTRAPMENT = "haz-working-at-height-mewp-operator-entrapment-or-crushing-overhead";
const MEWP_OVERTURN = "haz-working-at-height-mewp-overturn-on-soft-or-sloping-ground";
const OPERATOR_STRANDED = "haz-working-at-height-operator-stranded-at-height-requiring-rescue";
const LADDER_OVERREACH = "haz-working-at-height-ladder-overreaching-loss-of-balance";
const STEPLADDER_SIDELOAD = "haz-working-at-height-stepladder-toppling-from-side-load";

export const WAH_ACCESS_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "working-at-height": [
    {
      key: "accessMethod",
      label: "What access method will be used for the height work?",
      kind: "choice",
      options: ACCESS_OPTIONS,
      effects: Object.fromEntries(
        ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for work at height: ${o}.` }]),
      ),
    },
    {
      key: "ladderUse",
      label: "Will ladders or stepladders be used for access or short work?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [LADDER_INSTABILITY], scopeLine: "Ladders are used only for low-risk, short-duration work, justified against the WAHR hierarchy and secured against slipping." },
        no: { hazardsOff: [LADDER_INSTABILITY], scopeLine: "No ladders in use — access is by working platform; ladder controls are not required for this task." },
      },
      notSure: { hazardsOn: [LADDER_INSTABILITY], failCheck: "Ladder use confirmed" },
    },
    {
      key: "publicBelow",
      label: "Are other trades, occupants or the public below or near the work area?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FALLING_OBJECTS], requireFields: ["nearbyTradesOrPublic"], scopeLine: "People may be below the work — an exclusion zone, toe boards and tool tethering protect against falling objects." },
        no: { hazardsOff: [FALLING_OBJECTS], scopeLine: "No trades, occupants or public below the work area — the drop zone is closed off, so falling-object controls beyond housekeeping are not required." },
      },
      notSure: { hazardsOn: [FALLING_OBJECTS], failCheck: "People below the work confirmed" },
    },
    {
      key: "overhead",
      label: "Are overhead power lines within reach of the access equipment?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], emergencyOff: ["Overhead line contact"], scopeLine: "No overhead power lines within risk distance of the work area." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "weather",
      label: "Is the work exposed to wind or weather (external or rooftop)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [ADVERSE_WEATHER], scopeLine: "Work is weather-exposed — a wind-speed stop limit is set and the forecast monitored through the shift." },
        no: { hazardsOff: [ADVERSE_WEATHER], sequenceOff: [7], scopeLine: "Work is internal / sheltered — adverse-weather and wind stop controls are not required for this task." },
      },
      notSure: { hazardsOn: [ADVERSE_WEATHER], failCheck: "Weather exposure confirmed" },
    },
    {
      key: "methodSignoff",
      label: "Who signs off the access-method selection against the WAHR hierarchy?",
      kind: "text",
      placeholder: "e.g. J Smith, site supervisor / competent person",
      requiredCheck: "Access-method sign-off recorded",
      effects: { "*": { scopeLine: "Access method selected and signed off by: {answer}." } },
    },
    RESCUE_Q,
  ],

  "mobile-tower-scaffold": [
    {
      key: "buildMethod",
      label: "Which safe build method will be used?",
      kind: "choice",
      options: ["3T (through-the-trap)", "Advance guardrail (AGR)"],
      effects: {
        "3T (through-the-trap)": { scopeLine: "Tower built and dismantled by the 3T (through-the-trap) method — operatives never on an unguarded platform." },
        "Advance guardrail (AGR)": { scopeLine: "Tower built and dismantled by the advance-guardrail (AGR) method — guardrails locked in from the level below before stepping up." },
      },
    },
    {
      key: "freestanding",
      label: "Is the tower within its free-standing height limit, or will it be tied?",
      kind: "choice",
      options: ["Within free-standing limit with stabilisers/outriggers", "Tied to the structure for extra height"],
      effects: {
        "Within free-standing limit with stabilisers/outriggers": { scopeLine: "Tower kept within the manufacturer's free-standing limit with the specified stabilisers or outriggers fitted." },
        "Tied to the structure for extra height": { scopeLine: "Tower tied to the structure to a designed pattern where the free-standing limit would be exceeded." },
      },
    },
    {
      key: "overhead",
      label: "Are overhead power lines near the tower or its move route?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], emergencyOff: ["Overhead line contact"], scopeLine: "No overhead power lines within risk distance of the tower position or move route." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "publicBelow",
      label: "Are other trades, occupants or the public below or near the tower?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FALLING_OBJECTS], requireFields: ["nearbyTradesOrPublic"], scopeLine: "People may be near the tower — toe boards on all sides and a ground-level exclusion zone protect against falling objects." },
        no: { hazardsOff: [FALLING_OBJECTS], scopeLine: "No trades, occupants or public near the tower — falling-object controls beyond toe boards and housekeeping are not required." },
      },
      notSure: { hazardsOn: [FALLING_OBJECTS], failCheck: "People near the tower confirmed" },
    },
    {
      key: "inspectionTag",
      label: "Who completes the tower inspection tag, and when?",
      kind: "text",
      placeholder: "e.g. PASMA competent person, before first use and after each move",
      requiredCheck: "Tower inspection/tagging regime recorded",
      effects: { "*": { scopeLine: "Tower inspection and tagging: {answer}." } },
    },
    {
      key: "pasmaCard",
      label: "Will all assembly be by PASMA-carded operatives with in-date cards?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Assembly, alteration and dismantling by PASMA-carded operatives holding in-date cards." },
        no: { failCheck: "PASMA-competent assembly confirmed" },
      },
      notSure: { failCheck: "PASMA-competent assembly confirmed" },
    },
    RESCUE_Q,
  ],

  "mewp-cherry-picker": [
    {
      key: "mewpType",
      label: "What type of MEWP will be used?",
      kind: "choice",
      options: ["Scissor / vertical mast (IPAF 3a)", "Articulated or telescopic boom (IPAF 3b)"],
      effects: {
        "Scissor / vertical mast (IPAF 3a)": { scopeLine: "MEWP type: scissor / vertical mast, operated by an IPAF 3a operator. Guardrails are the primary fall protection; a restraint harness is not normally required in a scissor lift." },
        "Articulated or telescopic boom (IPAF 3b)": { scopeLine: "MEWP type: articulated / telescopic boom, operated by an IPAF 3b operator wearing a full-body harness with a short work-restraint lanyard to the basket anchor." },
      },
    },
    {
      key: "overheadCrush",
      label: "Is there work near overhead structures where the operator could be crushed?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MEWP_ENTRAPMENT], scopeLine: "Work near overhead structures — secondary guarding / anti-entrapment is used and the work zone planned to keep the basket clear of crushing points." },
        no: { hazardsOff: [MEWP_ENTRAPMENT], scopeLine: "No overhead crushing structures in the work zone — entrapment controls beyond standard operation are not required." },
      },
      notSure: { hazardsOn: [MEWP_ENTRAPMENT], failCheck: "Overhead crushing hazard confirmed" },
    },
    {
      key: "overhead",
      label: "Are overhead power lines within reach of the boom or basket?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], emergencyOff: ["Overhead line contact"], scopeLine: "No overhead power lines within slewing or elevating reach of the machine." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "traffic",
      label: "Will the machine work near pedestrian or vehicle routes?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN], requireFields: ["nearbyTradesOrPublic"], scopeLine: "Machine works near active routes — the work area, including the boom envelope, is segregated and a banksman used where visibility is restricted." },
        no: { hazardsOff: [PLANT_PEDESTRIAN], scopeLine: "Machine works in an area closed to pedestrian and vehicle traffic — route-segregation controls are not required for this task." },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Proximity to traffic routes confirmed" },
    },
    {
      key: "loler",
      label: "LOLER thorough examination — date / certificate reference",
      kind: "text",
      placeholder: "e.g. examined 12/03/2026, cert ref MEWP-4471",
      requiredCheck: "LOLER thorough examination evidence recorded",
      effects: { "*": { scopeLine: "LOLER thorough examination: {answer} (within the previous six months)." } },
    },
    {
      key: "groundRescue",
      label: "Is a trained ground person available to operate the emergency lowering controls?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "A trained ground person can operate the auxiliary/emergency lowering controls to recover a stranded operator." },
        no: { hazardsOn: [OPERATOR_STRANDED], failCheck: "Trained ground rescuer confirmed" },
      },
      notSure: { hazardsOn: [OPERATOR_STRANDED], failCheck: "Trained ground rescuer confirmed" },
    },
    RESCUE_Q,
  ],

  "ladders-stepladders": [
    {
      key: "ladderType",
      label: "What ladder type and justification applies to this task?",
      kind: "choice",
      options: [
        "Leaning ladder — short-duration access, secured",
        "Stepladder — low-risk task off a locked step",
        "Roof ladder — short-duration roof access",
      ],
      effects: {
        "Leaning ladder — short-duration access, secured": { hazardsOff: [STEPLADDER_SIDELOAD], sequenceOff: [5], scopeLine: "Leaning ladder for short-duration access, set at 1:4 and secured at the stiles; stepladder side-load controls do not apply." },
        "Stepladder — low-risk task off a locked step": { sequenceOff: [4], scopeLine: "Stepladder for a low-risk task, opened fully with the spreaders locked; no side-loading work undertaken from the steps." },
        "Roof ladder — short-duration roof access": { hazardsOff: [STEPLADDER_SIDELOAD], sequenceOff: [5], scopeLine: "Roof ladder for short-duration roof access, properly hooked over the ridge; stepladder side-load controls do not apply." },
      },
    },
    {
      key: "justification",
      label: "Why is a ladder justified over a platform (low risk + short duration)?",
      kind: "text",
      placeholder: "e.g. single fixing, under 15 min, sound footing, no platform access",
      requiredCheck: "Ladder justification recorded against the WAHR hierarchy",
      effects: { "*": { scopeLine: "Ladder justification (low risk, short duration): {answer}." } },
    },
    {
      key: "securing",
      label: "How will a leaning ladder be secured against slipping?",
      kind: "choice",
      options: [
        "Tied at the stiles near the top",
        "Proprietary stability device / stand-off",
        "Footed by a second person (short ladder only)",
        "Not using a leaning ladder",
      ],
      effects: {
        "Tied at the stiles near the top": { scopeLine: "Leaning ladder tied at the stiles near the top against slipping." },
        "Proprietary stability device / stand-off": { scopeLine: "Leaning ladder secured with a proprietary stability device / stand-off where tying is not possible." },
        "Footed by a second person (short ladder only)": { scopeLine: "Short leaning ladder footed by a second person where securing is not reasonably practicable.", failCheck: "Footing is the lowest control — confirm securing is genuinely not practicable" },
        "Not using a leaning ladder": { scopeLine: "No leaning ladder in use; only stepladders / roof ladders for this task." },
      },
    },
    {
      key: "publicBelow",
      label: "Are other trades, occupants or the public below or near the work?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FALLING_OBJECTS], requireFields: ["nearbyTradesOrPublic"], scopeLine: "People may be below — the area beneath the ladder is kept clear and barriered, and tools carried in a belt or raised on a line." },
        no: { hazardsOff: [FALLING_OBJECTS], scopeLine: "No trades, occupants or public below the work — falling-object controls beyond housekeeping are not required." },
      },
      notSure: { hazardsOn: [FALLING_OBJECTS], failCheck: "People below the work confirmed" },
    },
    {
      key: "preUseCheck",
      label: "Who carries out and records the pre-use ladder inspection?",
      kind: "text",
      placeholder: "e.g. user pre-use check + monthly recorded inspection by site supervisor",
      requiredCheck: "Pre-use ladder inspection regime recorded",
      effects: { "*": { scopeLine: "Ladder inspection: {answer}. Class 1 / EN 131 Professional equipment only." } },
    },
    RESCUE_Q,
  ],
};
